import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import MainNavPanel from './MainNavPanel';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import IncidentNavPanel from './IncidentNavPanel';
import ExerciseNavPanel from './ExerciseNavPanel';
import TaskManagementNavPanel from './TaskManagementNavPanel';
import { useClickAway, useFirstMountState } from 'react-use';
import { useSidebarNavContext } from './SidebarNavProvider';
import dayjs from 'dayjs';
import IncidentWorkspacePanel from './IncidentWorkspacePanel';
import { useLocation, useMatch } from 'react-router';
import NotificationsNavPanel from './NotificationsNavPanel';
import SitrepsPanel from './SitrepsPanel';

export type PanelType =
  | 'incident'
  | 'ics'
  | 'exercise'
  | 'task'
  | 'notifications'
  | 'sitreps'
  | null;

/** TODO: check scrollbars in Windows/Firefox/Safari */
const SidebarNav: React.FC<{
  toggleRef: MutableRefObject<HTMLButtonElement | null>;
}> = ({ toggleRef }) => {
  const { close, isOpen } = useSidebarNavContext();
  const location = useLocation();

  const [secondPanel, setSecondPanel] = useState<PanelType | null>(null);
  const navRef = useRef(null);

  useClickAway(navRef, (e) => {
    if (isOpen && !toggleRef.current?.contains(e.target as Node)) {
      close();
    }
  });

  const isFirstMount = useFirstMountState();
  const mapMatch = useMatch({ path: '/map', end: false });
  const iapMatch = useMatch({ path: '/iap', end: false });
  const taskMatch = useMatch({ path: '/task_management', end: false });
  const prepareMatch = useMatch({ path: '/prepare', end: false });
  const notificationsMatch = useMatch({ path: '/notifications', end: false });
  const sitrepsMatch = useMatch({ path: '/sitreps', end: false });

  useEffect(() => {
    /**
     * NOTE: right now only checking map since there are "Go to Map" buttons.
     * Might make sense to do this and get rid of the "setSecondPanel" pass down
     * prop.
     */
    if (mapMatch) {
      setSecondPanel(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname,]);

  if (isFirstMount) {
    if (iapMatch) {
      setSecondPanel('ics');
    } else if (taskMatch) {
      setSecondPanel('task');
    } else if (prepareMatch) {
      setSecondPanel('exercise');
    } else if (notificationsMatch) {
      setSecondPanel('notifications');
    } else if (sitrepsMatch) {
      setSecondPanel('sitreps');
    }
  }

  return (
    <div id="sidebar-nav" className={isOpen ? 'open' : ''} ref={navRef}>
      <div className="sidebar-nav-content">
        <SwitchTransition>
          <CSSTransition
            key={secondPanel ? 'MainNavPanel' : 'SecondPanel'}
            classNames="nav-slide"
            timeout={300}
          >
            {!secondPanel ? (
              <MainNavPanel
                setSecondPanel={(key: PanelType) => setSecondPanel(key)}
              />
            ) : (
              <SecondPanelSwitch
                secondPanel={secondPanel}
                navBack={() => setSecondPanel(null)}
              />
            )}
          </CSSTransition>
        </SwitchTransition>
      </div>
      <div className="sidebar-nav-footer flex-shrink-0 flex-grow-0">
        © {dayjs().format('YYYY')} PRATUS • All rights reserved.
      </div>
    </div>
  );
};

export default SidebarNav;

const SecondPanelSwitch: React.FC<{
  secondPanel: PanelType;
  navBack: () => void;
}> = ({ secondPanel, navBack }) => {
  switch (secondPanel) {
    case 'incident':
      return <IncidentNavPanel navBack={navBack} />;
    case 'exercise':
      return <ExerciseNavPanel navBack={navBack} />;
    case 'task':
      return <TaskManagementNavPanel navBack={navBack} />;
    case 'ics':
      return <IncidentWorkspacePanel navBack={navBack} />;
    case 'notifications':
      return <NotificationsNavPanel navBack={navBack} />;
    case 'sitreps':
      return <SitrepsPanel navBack={navBack} />;
    default:
      return null;
  }
};
